import { gql, useMutation } from "@apollo/client";
import { PaymentMethodData } from "../../../../../__generated__/globalTypes";
import {
  InitMultiBind,
  InitMultiBindVariables,
} from "../../../../../__generated__/InitMultiBind";
import { useSignupDispatch } from "components/Signup/store/hooks";
import { updateMetadata } from "components/Signup/store/slices/metadata";
import { useWebTranslation } from "translations";
import setupStripe from "./stripe/setupStripe";
import { Stripe, StripeElements } from "@stripe/stripe-js";
import { SelectablePaymentMethodEnum } from "config/site";
import { convertToPaymentMethod } from "utils/payment-method";
import { PaymentResult } from "./types";
import { useCustomer } from "components/Signup/store/selectors/customer";

type UseInitMultiBindProps = {
  stripe: Stripe | null;
  elements: StripeElements | null;
};

type InitMultiBindProps = {
  draftId: string | null;
  paymentMethod: SelectablePaymentMethodEnum;
  paymentData?: PaymentMethodData;
};

function useInitMultiBind({ stripe, elements }: UseInitMultiBindProps) {
  const { t } = useWebTranslation();
  const dispatch = useSignupDispatch();
  const customer = useCustomer();

  const [multiBindDrafts] = useMutation<InitMultiBind, InitMultiBindVariables>(
    INIT_MULTI_BIND
  );

  async function initMultiBind({
    draftId,
    paymentMethod,
    paymentData,
  }: InitMultiBindProps) {
    let res: { result: PaymentResult | null; error: Error | null } = {
      result: null,
      error: new Error(t("payment.error_unknown")),
    };

    if (!draftId) {
      return res;
    }

    dispatch(updateMetadata({ loading: true }));

    try {
      const { data } = await multiBindDrafts({
        variables: {
          input: {
            draftId,
            paymentMethod: convertToPaymentMethod(paymentMethod),
            data: paymentData,
          },
        },
      });

      if (data && data.initMultiBind) {
        switch (data.initMultiBind.__typename) {
          case "SetupPaymentMethodResultStripe":
            {
              const result = await setupStripe({
                stripe,
                elements,
                clientSecret: data.initMultiBind.clientSecret ?? "",
                billingDetails: {
                  name: `${customer.firstName} ${customer.lastName}`,
                  email: customer.email ?? "",
                  phone: customer.phone ?? "",
                },
              });

              res = { result, error: null };
            }
            break;

          case "SetupPaymentMethodResultTrustly":
            res = {
              result: {
                error: null,
                redirectUrl: data.initMultiBind.url ?? "",
              },
              error: null,
            };
            break;

          default: {
            res = {
              result: null,
              error: new Error(t("payment.error_payment_start")),
            };
          }
        }
      }
    } catch (e) {
      res = {
        result: null,
        error: new Error(t("payment.error_unknown")),
      };
    }

    dispatch(updateMetadata({ loading: false }));

    return res;
  }

  return initMultiBind;
}

const INIT_MULTI_BIND = gql`
  mutation InitMultiBind($input: InitMultiBindInput) {
    initMultiBind(input: $input) {
      ... on SetupPaymentMethodResultStripe {
        clientSecret
      }
      ... on SetupPaymentMethodResultTrustly {
        url
      }
    }
  }
`;

export default useInitMultiBind;
